import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES, useSWRAlloy } from '@adsk/alloy-helpers';
import { getOrCreateGlobalContext, useContextWithValidation } from '@adsk/alloy-react-context';
import { PROJECT_CLASSIFICATION_TYPES } from './consts';
import { useCurrentProjectId } from './CurrentProjectIdContext';
import { fetchWithAccountType } from './helpers/fetchWithAccountType';
import isProjectTemplate from './helpers/isProjectTemplate';
import { useProviderOnlyOnce } from './hooks/useProviderOnlyOnce';
/**
 * Context for project entitlements.
 */
export const ProjectEntitlementsContext = getOrCreateGlobalContext(CONTEXT_NAMES.ProjectEntitlementsContext);
export const useProjectEntitlements = () => useContextWithValidation(ProjectEntitlementsContext, {
  hook: 'useProjectEntitlements',
  provider: 'ProjectEntitlementsProvider'
});

/**
 * Hook for getting the current project the user work in.
 * This hook returns the current project, as returned from [clients](https://platform.stoplight.autodesk.com/docs/hq-api-server/f3dfd32ac6afb-list-clients)
 request.
 * Return undefined if project entitlements are missing.
 * @returns {undefined|EntitlementsProject} The project entitlements or undefined
 * @example
 * import { useCurrentProject } from '@adsk/alloy-provider';
 * ...
 * const currentProject = useCurrentProject();
 */
export const useCurrentProject = () => {
  const {
    currentProject
  } = useProjectEntitlements();
  return currentProject;
};

/**
 * Hook for getting the current logged in user.
 * This hook returns the current user, as returned from [clients](https://platform.stoplight.autodesk.com/docs/hq-api-server/f3dfd32ac6afb-list-clients)
 request.
 * @returns {{ user: EntitlementsUser, setUser: (user: EntitlementsUser) => void }} An object of shape {user, setUser} - containing the current user and setUser function to update the current user.
 * @example
 * import { useUser } from '@adsk/alloy-provider';
 * ...
 * const { user, setUser } = useUser();
 */
export const useUser = () => {
  const {
    user,
    setUser
  } = useProjectEntitlements();
  return useMemo(() => user ? {
    user,
    setUser
  } : {}, [user, setUser]);
};

/**
 * This hook returns whether or not the current project is a template.
 * @returns {boolean} Whether the current project entitlements is a template or not.
 * @example
 * import { useCurrentProjectIsTemplate } from '@adsk/alloy-provider';
 * ...
 * const isTemplate = useCurrentProjectIsTemplate();
 */
export const useCurrentProjectIsTemplate = () => {
  const currentProject = useCurrentProject();
  return currentProject ? isProjectTemplate(currentProject) : false;
};
const fields = ['accountId', 'name', 'accountName', 'accountImageUrl', 'defaultUrl', 'classification', 'imageUrl', 'region', 'products', 'platform', 'mfaBlocked'];
const classificationFilter = "filter[classification]=".concat(Object.values(PROJECT_CLASSIFICATION_TYPES).join(','));
const getCurrentProjectIdPart = projectId => projectId ? "currentProjectId=".concat(projectId, "&") : '';
/**
 * @summary Considerations for Feature Flag Usage within ProjectEntitlementsProvider
 * @description When working with feature flags within providers, follow these guidelines to prevent unexpected issues.
 *
 * @instructions
 * **Context Data Dependencies:**
 *  - Before modifying feature flags within a provider, assess dependencies on context data.
 *  - When feature flag is using targeting rules, it may be dependent on context data, context data isn't available until clients are fetched.
 *  - Understand potential impacts on provider behavior, especially in interaction with other components.
 */
export const ProjectEntitlementsProvider = _ref => {
  let {
    children
  } = _ref;
  const projectId = useCurrentProjectId();
  const [user, setUser] = useState(undefined);
  const [currentProject, setCurrentProject] = useState(undefined);
  useProviderOnlyOnce({
    context: ProjectEntitlementsContext,
    contextName: CONTEXT_NAMES.ProjectEntitlementsContext
  });
  const {
    data,
    error,
    revalidate: retry,
    clearSessionStorage
  } = useSWRAlloy("".concat(getAdminApiUrl(), "/ui/v1/clients?").concat(getCurrentProjectIdPart(projectId), "fields=").concat(fields, "&").concat(classificationFilter, "&limit=1"), fetchWithAccountType);
  useEffect(() => {
    if (error && error.status !== 401) {
      // 401 errors are pretty common and expected, here we want to mostly log unexpected ones
      console.error('Error calling project entitlements', error);
    }
  }, [error]);
  const setCurrentProjectAndClearCache = useCallback(project => {
    setCurrentProject(project);
    clearSessionStorage();
  }, [setCurrentProject, clearSessionStorage]);
  const value = useMemo(() => ({
    loading: !error && !data,
    error,
    retry,
    user: user || (data === null || data === void 0 ? void 0 : data.user),
    setUser,
    currentProject: currentProject || (data === null || data === void 0 ? void 0 : data.currentProject),
    setCurrentProject: setCurrentProjectAndClearCache
  }), [error, data, currentProject, user, retry, setCurrentProjectAndClearCache]);
  return /*#__PURE__*/React.createElement(ProjectEntitlementsContext.Provider, {
    value: value
  }, children);
};
ProjectEntitlementsProvider.displayName = 'ProjectEntitlementsProvider';
ProjectEntitlementsProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node
};
//# sourceMappingURL=ProjectEntitlementsContext.js.map